// Now Playing (panel view) — large artwork, metadata, transport, seek, shuffle/repeat,
// and the action row. Provider-neutral via usePlayer. Used inside the right-side panel and
// reusable for a full-screen route later.
import React, { useState } from 'react';
import { Shuffle, SkipBack, SkipForward, Play, Pause, Repeat, Repeat1, Quote, ListMusic, ExternalLink, Music2, ChevronLeft, Loader2 } from 'lucide-react';
import { openProviderUrl } from '@/lib/providerLinks';
import { appleMusicProvider } from '../lib/appleMusicProvider';
import type { MusicItem } from '../lib/types';
import { usePlayer } from './usePlayer';

const fmt = (ms: number) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const provider = appleMusicProvider();

export const NowPlaying: React.FC = () => {
  const { snap, toggle, next, prev, seek, shuffle, repeat, togglePanel, playItem } = usePlayer();
  const item = snap.item;
  const artwork = provider.artworkUrl(item?.artwork, 600);
  const playing = snap.status === 'playing';
  const nextRepeat = snap.repeat === 'off' ? 'all' : snap.repeat === 'all' ? 'one' : 'off';

  // "More from this artist" — clicking the artwork/artist resolves the artist's top songs
  // (search → getArtist top-songs) and lists them, playable inline.
  const [artistView, setArtistView] = useState(false);
  const [artistSongs, setArtistSongs] = useState<MusicItem[]>([]);
  const [artistLoading, setArtistLoading] = useState(false);

  const openArtist = async () => {
    if (!item?.artist) return;
    setArtistView(true); setArtistLoading(true); setArtistSongs([]);
    try {
      const res = await provider.searchCatalog(item.artist, ['artists', 'songs'], { limit: 12 });
      let songs = res.songs || [];
      const artist = res.artists?.[0];
      if (artist?.id) {
        try { const d = await provider.getArtist(artist.id); if (d.topSongs?.length) songs = d.topSongs; } catch { /* fall back to search songs */ }
      }
      setArtistSongs(songs.filter((s) => s.id !== item.id).slice(0, 30));
    } catch { setArtistSongs([]); }
    finally { setArtistLoading(false); }
  };

  if (artistView) {
    return (
      <div className="flex h-full flex-col">
        <button onClick={() => setArtistView(false)} className="mb-2 flex items-center gap-1 self-start text-sm text-primary-600 hover:underline"><ChevronLeft className="h-4 w-4" /> Now playing</button>
        <div className="mb-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">More from</div>
          <div className="truncate text-lg font-bold text-foreground" dir="auto">{item?.artist}</div>
        </div>
        {artistLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : artistSongs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other songs found for this artist.</p>
        ) : (
          <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
            {artistSongs.map((s) => {
              const art = provider.artworkUrl(s.artwork, 96);
              return (
                <li key={s.id}>
                  <button type="button" onClick={() => void playItem(s)}
                    className="group flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded bg-muted">
                      {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Music2 className="h-4 w-4 text-muted-foreground" /></div>}
                      <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition group-hover:opacity-100"><Play className="h-4 w-4 text-white" /></span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground" dir="auto">{s.title}</div>
                      <div className="truncate text-xs text-muted-foreground" dir="auto">{s.album || s.artist}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-2 pt-2 text-center text-[11px] text-muted-foreground">Powered by Apple Music</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <button type="button" onClick={openArtist} disabled={!item?.artist} title={item?.artist ? `More from ${item.artist}` : undefined}
        className="group relative mx-auto aspect-square w-full max-w-[300px] overflow-hidden rounded-2xl bg-muted shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
        {artwork ? <img src={artwork} alt="" className="h-full w-full object-cover" />
          : <div className="flex h-full items-center justify-center"><Music2 className="h-16 w-16 text-muted-foreground/40" /></div>}
        {item?.artist && (
          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-gradient-to-t from-black/60 to-transparent py-2 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100">
            <Music2 className="h-3.5 w-3.5" /> More from {item.artist}
          </span>
        )}
      </button>

      <div className="mt-4 text-center">
        <div className="truncate text-lg font-bold text-foreground" dir="auto">{item?.title || 'Nothing playing'}</div>
        <button type="button" onClick={openArtist} disabled={!item?.artist}
          className="mx-auto block max-w-full truncate text-sm text-muted-foreground hover:text-foreground hover:underline disabled:no-underline disabled:hover:text-muted-foreground" dir="auto">
          {item?.artist}{item?.album ? ` — ${item.album}` : ''}
        </button>
      </div>

      <div className="mt-4">
        <input
          type="range" min={0} max={snap.durationMs || 0} value={snap.positionMs}
          onChange={(e) => seek(Number(e.target.value) / 1000)}
          aria-label="Seek" disabled={!item}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-orange-500"
        />
        <div className="mt-1 flex justify-between text-[11px] tabular-nums text-muted-foreground">
          <span>{fmt(snap.positionMs)}</span><span>{fmt(snap.durationMs)}</span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-center gap-5">
        <IconBtn label="Shuffle" active={snap.shuffle} onClick={() => shuffle(!snap.shuffle)}><Shuffle className="h-5 w-5" /></IconBtn>
        <IconBtn label="Previous" onClick={prev}><SkipBack className="h-6 w-6" /></IconBtn>
        <button type="button" aria-label={playing ? 'Pause' : 'Play'} onClick={toggle}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-white shadow-md transition hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
          {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 translate-x-0.5" />}
        </button>
        <IconBtn label="Next" onClick={next}><SkipForward className="h-6 w-6" /></IconBtn>
        <IconBtn label={`Repeat: ${snap.repeat}`} active={snap.repeat !== 'off'} onClick={() => repeat(nextRepeat)}>
          {snap.repeat === 'one' ? <Repeat1 className="h-5 w-5" /> : <Repeat className="h-5 w-5" />}
        </IconBtn>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        <ActionBtn label="Lyrics" onClick={() => togglePanel('lyrics')}><Quote className="h-4 w-4" /></ActionBtn>
        <ActionBtn label="Queue" onClick={() => togglePanel('queue')}><ListMusic className="h-4 w-4" /></ActionBtn>
        {item?.canonicalUrl && <ActionBtn label="Open in Apple Music" onClick={() => openProviderUrl(item.canonicalUrl!)}><ExternalLink className="h-4 w-4" /></ActionBtn>}
      </div>
      <p className="mt-auto pt-3 text-center text-[11px] text-muted-foreground">Powered by Apple Music</p>
    </div>
  );
};

const IconBtn: React.FC<{ label: string; active?: boolean; onClick: () => void; children: React.ReactNode }> = ({ label, active, onClick, children }) => (
  <button type="button" aria-label={label} aria-pressed={active} onClick={onClick}
    className={`rounded-full p-1.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${active ? 'text-primary-500' : 'text-muted-foreground hover:text-foreground'}`}>
    {children}
  </button>
);
const ActionBtn: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
    {children}<span className="hidden sm:inline">{label}</span>
  </button>
);
